import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Banner,
  Button,
  Card,
  Combobox,
  FileUpload,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useMaxUploadBytes, useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { makeAttachmentsApi } from '../attachments/attachmentsApi';
import { catalogApi } from '../catalog/catalogApi';
import { useCatalogItemPicker } from '../catalog/useCatalogItemPicker';
import { settingsApi } from '../settings/settingsApi';
import { suppliersApi } from '../suppliers/suppliersApi';
import { requisitionPath } from '../../lib/slug';
import { requisitionsApi, REQ_PRIORITIES } from './requisitionsApi';
import BudgetPanel from '../budgets/BudgetPanel';
import './requisitions.css';

let tmpId = 0;
const newLine = (over = {}) => ({
  key: `tmp-${++tmpId}`,
  itemId: '',
  description: '',
  sku: '',
  quantity: '',
  uomId: '',
  estimatedUnitPrice: '',
  supplierId: '',
  costCenter: '',
  glAccount: '',
  notes: '',
  ...over,
});

// A row only counts as "real" once it references an item or has a description.
const isBlankLine = (l) => !l.itemId && !l.description.trim();

export default function RequisitionFormPage() {
  const { id: routeRef } = useParams();
  const editing = Boolean(routeRef);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { activePropertyId } = useProperty();
  const { maxMb } = useMaxUploadBytes();

  // Resolved real id of the requisition being edited (the URL carries the serial number).
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);

  // A new requisition has no id yet, so files are held here and uploaded the moment the draft
  // exists. That keeps "Save & submit" a single action even when a file is mandatory.
  const [stagedFiles, setStagedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState(0);
  const [requireAttachment, setRequireAttachment] = useState(false);

  const itemPicker = useCatalogItemPicker({ propertyId: activePropertyId });
  const items = itemPicker.items;
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState(['USD']);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  // Cache of item -> linked suppliers (price list), so we can filter & autofill per line.
  const [itemSuppliers, setItemSuppliers] = useState({});
  const itemSuppliersRef = useRef({});

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [locationId, setLocationId] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([newLine()]);

  const loadLookups = useCallback(async () => {
    try {
      const [us, locs, sups, eff, reqRules] = await Promise.all([
        catalogApi.listUnits(),
        catalogApi.listLocations(activePropertyId),
        suppliersApi.list({ pageSize: 500, status: 'Active' }).catch(() => null),
        settingsApi.getEffective(activePropertyId).catch(() => null),
        settingsApi.getEffectiveRequisitions(activePropertyId).catch(() => null),
      ]);
      setRequireAttachment(reqRules?.requireAttachmentOnSubmit === true);
      setUnits(us || []);
      setLocations(locs || []);
      setSuppliers(sups?.items || (Array.isArray(sups) ? sups : []));
      const ccys = eff?.currencies?.length ? eff.currencies : ['USD'];
      setCurrencies(ccys);
      setCostCenters((eff?.costCenters || []).filter((c) => c.isActive !== false));
      setGlAccounts((eff?.glAccounts || []).filter((g) => g.isActive !== false));
      if (!editing) setCurrency(eff?.defaultCurrency || ccys[0] || 'USD');
    } catch {
      // Non-fatal; the form still works with free-text lines and USD.
    }
  }, [activePropertyId, editing]);

  const loadRequisition = useCallback(async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const r = await requisitionsApi.get(routeRef);
      setEditingId(r.id);
      if (r.status !== 'Draft' && r.status !== 'PendingApproval') {
        toast.error(t('Only draft or pending-approval requisitions can be edited.'));
        navigate(requisitionPath(r));
        return;
      }
      setTitle(r.title || '');
      setPriority(r.priority || 'Normal');
      setLocationId(r.locationId || '');
      setNeededBy(r.neededByDate ? r.neededByDate.slice(0, 10) : '');
      setCurrency(r.currency || 'USD');
      setNotes(r.notes || '');
      setLines([
        ...(r.lines || []).map((l) => newLine({
          itemId: l.itemId || '',
          description: l.description || '',
          sku: l.sku || '',
          quantity: l.quantity ?? '',
          uomId: l.uomId || '',
          estimatedUnitPrice: l.estimatedUnitPrice?.amount ?? '',
          supplierId: l.preferredSupplierId || '',
          costCenter: l.costCenter || '',
          glAccount: l.glAccountCode || '',
          notes: l.notes || '',
        })),
        newLine(),
      ]);

      // Files already on the requisition satisfy the rule, so the count decides whether the
      // form still has to insist on one.
      try {
        const existing = await makeAttachmentsApi(`/requisitions/${r.id}/attachments`).list();
        setExistingAttachments(Array.isArray(existing) ? existing.length : 0);
      } catch {
        setExistingAttachments(0);
      }
    } catch (err) {
      toast.error(t('Could not load requisition'), { description: err?.message });
      navigate('/requisitions');
    } finally {
      setLoading(false);
    }
  }, [editing, routeRef, navigate, toast, t]);

  // Fetch (and cache) the suppliers linked to an item's price list.
  const loadItemSuppliers = useCallback(async (itemId) => {
    if (!itemId) return [];
    if (itemSuppliersRef.current[itemId]) return itemSuppliersRef.current[itemId];
    let list = [];
    try { list = (await catalogApi.listItemSuppliers(itemId)) || []; } catch { list = []; }
    itemSuppliersRef.current = { ...itemSuppliersRef.current, [itemId]: list };
    setItemSuppliers(itemSuppliersRef.current);
    return list;
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadRequisition(); }, [loadRequisition]);

  // Prefetch price lists — and the items themselves — for lines already on the (edited) requisition.
  useEffect(() => {
    const ids = [...new Set(lines.map((l) => l.itemId).filter(Boolean))];
    ids.forEach((id2) => { if (!itemSuppliersRef.current[id2]) loadItemSuppliers(id2); });
    itemPicker.ensureIds(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, loadItemSuppliers]);

  const itemOptions = itemPicker.options;
  const uomOptions = useMemo(
    () => [{ value: '', label: '—' }, ...units.map((u) => ({ value: u.id, label: u.code || u.name }))],
    [units],
  );
  const supplierOptions = useMemo(
    () => [{ value: '', label: t('— Unassigned —') }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))],
    [suppliers, t],
  );
  const supplierName = useCallback((sid) => suppliers.find((s) => s.id === sid)?.name || sid, [suppliers]);

  // Per-line supplier options: the item's linked suppliers come first (preferred starred, with price),
  // then any remaining suppliers — so users can still pick an off-catalog source if needed.
  const supplierOptionsForLine = useCallback((line) => {
    const links = (itemSuppliers[line.itemId] || []).filter((s) => s.isActive);
    if (!line.itemId || links.length === 0) return supplierOptions;
    const linkedIds = new Set(links.map((s) => s.supplierId));
    const linkedOpts = links.map((s) => ({
      value: s.supplierId,
      label: `${supplierName(s.supplierId)}${s.isPreferred ? ' ★' : ''}`,
      description: s.price ? `${s.price.currency} ${formatAmount(s.price.amount)}` : t('Supplies this item'),
    }));
    const others = suppliers
      .filter((su) => !linkedIds.has(su.id))
      .map((su) => ({ value: su.id, label: su.name, description: t('Not on price list') }));
    return [{ value: '', label: t('— Unassigned —') }, ...linkedOpts, ...others];
  }, [itemSuppliers, suppliers, supplierOptions, supplierName, t]);
  const locationOptions = useMemo(
    () => [{ value: '', label: t('— No location —') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );
  const currencyOptions = useMemo(() => currencies.map((c) => ({ value: c, label: c })), [currencies]);
  // Per-line coding option lists.
  const lineCostCenterOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...costCenters.map((c) => ({ value: c.code, label: c.name ? `${c.code} · ${c.name}` : c.code }))],
    [costCenters, t],
  );
  const lineGlAccountOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...glAccounts.map((g) => ({ value: g.code, label: g.name ? `${g.code} · ${g.name}` : g.code }))],
    [glAccounts, t],
  );

  // Keep exactly one trailing blank row so users can always type the next line.
  const withTrailingBlank = (next) => {
    const trimmed = [...next];
    while (trimmed.length > 1 && isBlankLine(trimmed[trimmed.length - 1]) && isBlankLine(trimmed[trimmed.length - 2])) {
      trimmed.pop();
    }
    if (trimmed.length === 0 || !isBlankLine(trimmed[trimmed.length - 1])) trimmed.push(newLine());
    return trimmed;
  };

  const updateLine = (key, patch) =>
    setLines((prev) => withTrailingBlank(prev.map((l) => (l.key === key ? { ...l, ...patch } : l))));

  // After an item's price list loads, snap the line to the preferred/only supplier and fill price & unit.
  const applyItemSupplierDefaults = (key, itemId) => {
    const links = (itemSuppliersRef.current[itemId] || []).filter((s) => s.isActive);
    if (links.length === 0) return;
    const stdCost = items.find((i) => i.id === itemId)?.standardCost?.amount;
    const preferred = links.find((s) => s.isPreferred) || (links.length === 1 ? links[0] : null);
    setLines((prev) => withTrailingBlank(prev.map((l) => {
      if (l.key !== key || l.itemId !== itemId) return l;
      const supplierId = l.supplierId || preferred?.supplierId || '';
      const src = links.find((s) => s.supplierId === supplierId);
      if (!src) return { ...l, supplierId };
      // The supplier's negotiated price beats the item's standard-cost prefill, but never a user's own entry.
      const overridable = l.estimatedUnitPrice === '' || (stdCost != null && Number(l.estimatedUnitPrice) === stdCost);
      return {
        ...l,
        supplierId,
        uomId: l.uomId || src.purchaseUomId || l.uomId,
        estimatedUnitPrice: overridable && src.price ? src.price.amount : l.estimatedUnitPrice,
      };
    })));
  };

  const selectItem = (key, itemId) => {
    const item = items.find((i) => i.id === itemId);
    setLines((prev) => withTrailingBlank(prev.map((l) => {
      if (l.key !== key) return l;
      if (!item) return { ...l, itemId: '' };
      return {
        ...l,
        itemId: item.id,
        description: l.description?.trim() ? l.description : item.name,
        sku: item.sku || '',
        uomId: l.uomId || item.stockUomId || '',
        quantity: l.quantity === '' ? 1 : l.quantity,
        estimatedUnitPrice: l.estimatedUnitPrice === '' ? (item.standardCost?.amount ?? '') : l.estimatedUnitPrice,
        supplierId: l.supplierId || item.preferredSupplierId || '',
        // Pre-fill line coding from the item's defaults, but never clobber a manual choice.
        costCenter: l.costCenter || item.defaultCostCenter || '',
        glAccount: l.glAccount || item.defaultGlAccountCode || '',
      };
    })));
    if (item) loadItemSuppliers(item.id).then(() => applyItemSupplierDefaults(key, item.id));
  };

  // Choosing a linked supplier pulls in that source's price & purchase unit (overridable afterwards).
  const selectLineSupplier = (key, supplierId) => {
    setLines((prev) => withTrailingBlank(prev.map((l) => {
      if (l.key !== key) return l;
      const src = (itemSuppliersRef.current[l.itemId] || []).find((s) => s.supplierId === supplierId);
      if (!src) return { ...l, supplierId };
      return {
        ...l,
        supplierId,
        uomId: src.purchaseUomId || l.uomId,
        estimatedUnitPrice: src.price ? src.price.amount : l.estimatedUnitPrice,
      };
    })));
  };

  const removeLine = (key) =>
    setLines((prev) => withTrailingBlank(prev.filter((l) => l.key !== key)));

  const addBlankLine = () => setLines((prev) => [...prev, newLine()]);

  const lineTotal = (l) => {
    const p = parseFloat(l.estimatedUnitPrice);
    const q = parseFloat(l.quantity);
    if (Number.isNaN(p) || Number.isNaN(q)) return null;
    return p * q;
  };
  const realLines = lines.filter((l) => !isBlankLine(l));
  const grandTotal = realLines.reduce((sum, l) => sum + (lineTotal(l) || 0), 0);
  const money = (n) => `${currency} ${formatAmount(n)}`;

  // Budget projection groups: each line attributed to its own coding.
  const codingGroups = realLines
    .map((l) => ({
      costCenter: l.costCenter,
      glAccountCode: l.glAccount,
      amount: lineTotal(l) || 0,
    }))
    .filter((g) => g.costCenter);

  // A non-numeric entry would otherwise serialize as NaN -> null and the API would reject the whole
  // request with a JSON binding error naming a C# type, which tells the user nothing.
  const num = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

  const buildPayload = () => ({
    title: title.trim(),
    priority,
    locationId: locationId || null,
    neededByDate: neededBy ? new Date(neededBy).toISOString() : null,
    currency: (currency || 'USD').toUpperCase(),
    notes: notes.trim() || null,
    lines: realLines.map((l) => ({
      itemId: l.itemId || null,
      description: l.description.trim(),
      sku: l.sku?.trim() || null,
      quantity: num(l.quantity),
      uomId: l.uomId || null,
      estimatedUnitPrice:
        l.estimatedUnitPrice === '' || l.estimatedUnitPrice == null || !Number.isFinite(Number(l.estimatedUnitPrice))
          ? null
          : Number(l.estimatedUnitPrice),
      preferredSupplierId: l.supplierId || null,
      glAccountCode: l.glAccount || null,
      costCenter: l.costCenter || null,
      notes: l.notes?.trim() || null,
    })),
  });

  const attachmentCount = existingAttachments + stagedFiles.length;

  /** Collects every problem at once and names the offending row, so one save shows the full list. */
  const validate = (submitAfter) => {
    const problems = [];
    if (submitAfter && requireAttachment && attachmentCount === 0)
      problems.push(t('Attach at least one file (a photo, quote or specification) before submitting.'));
    if (!title.trim()) problems.push(t('Give the requisition a title.'));
    if (realLines.length === 0) problems.push(t('Add at least one line before saving.'));
    if (title.trim().length > 200) problems.push(t('The title is too long (max 200 characters).'));
    realLines.forEach((l, i) => {
      const n = i + 1;
      const description = l.description.trim();
      if (!description) problems.push(t('Line {n} needs a description.', { n }));
      else if (description.length > 500) problems.push(t('The description on line {n} is too long (max 500 characters).', { n }));
      if (!(Number(l.quantity) > 0)) problems.push(t('Line {n} needs a quantity greater than 0.', { n }));
      if (l.estimatedUnitPrice !== '' && Number(l.estimatedUnitPrice) < 0)
        problems.push(t('The estimated price on line {n} cannot be negative.', { n }));
    });
    if (problems.length === 0) return true;
    toast.error(t('Check these before saving'), { description: problems.join('\n') });
    return false;
  };

  const save = async (submitAfter) => {
    if (!validate(submitAfter)) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const saved = editing ? await requisitionsApi.update(editingId, payload) : await requisitionsApi.create(payload);

      // The draft now has an id, so anything the user picked can be attached to it. A failure here
      // must not be swallowed: the requisition exists, and submitting without its evidence would
      // either be refused by the server or reach an approver with nothing to look at.
      if (stagedFiles.length > 0) {
        const attachments = makeAttachmentsApi(`/requisitions/${saved.id}/attachments`);
        try {
          for (const file of stagedFiles) {
            await attachments.upload(file, { attachmentType: 'Other' });
          }
          setStagedFiles([]);
        } catch (err) {
          toast.error(t('{number} was saved, but a file could not be uploaded', { number: saved.number }), {
            description: err?.message,
          });
          navigate(requisitionPath(saved));
          return;
        }
      }

      if (submitAfter) {
        const submitted = await requisitionsApi.submit(saved.id);
        toast.success(t('{number} submitted for approval', { number: saved.number }));
        if (submitted?.budgetWarning) {
          toast.warning(t('Submitted over budget'), { description: submitted.budgetWarning });
        }
      } else {
        toast.success(editing ? t('Requisition updated') : t('{number} created', { number: saved.number }));
      }
      navigate(requisitionPath(saved));
    } catch (err) {
      toast.error(t('Could not save requisition'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/requisitions')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Requisitions')}
      </Button>

      <PageHeader
        title={editing ? t('Edit requisition') : t('New requisition')}
        description={t('Request goods for a property/location. Submit it to route for approval.')}
        icon={<Icon.ListChecks size={22} />}
      />

      <div className="req-form">
        <Card>
          <Card.Body>
            <div className="req-form">
              <Input label={t('Title')} placeholder={t('e.g. Weekend bar restock')} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
              <div className="req-form__grid">
                <div className="ps-field">
                  <span className="ps-field__label">{t('Priority')}</span>
                  <Select value={priority} onChange={setPriority} options={REQ_PRIORITIES.map((p) => ({ value: p.value, label: t(p.label) }))} ariaLabel={t('Priority')} />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Deliver-to location')}</span>
                  <Select value={locationId} onChange={setLocationId} options={locationOptions} ariaLabel={t('Location')} searchable />
                </div>
                <Input label={t('Needed by')} type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
                <div className="ps-field">
                  <span className="ps-field__label">{t('Currency')}</span>
                  <Select value={currency} onChange={setCurrency} options={currencyOptions} ariaLabel={t('Currency')} />
                </div>
              </div>
              <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Context for approvers (optional)')} />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Line items')}</Card.Title></Card.Header>
          <Card.Body>
            <div className="req-doc">
              <div className="req-doc__head">
                <span className="req-doc__col-num">#</span>
                <span>{t('Item')}</span>
                <span>{t('Description')}</span>
                <span>{t('Supplier')}</span>
                <span className="req-doc__num">{t('Qty')}</span>
                <span>{t('Unit')}</span>
                <span className="req-doc__num">{t('Unit price')}</span>
                <span className="req-doc__num">{t('Amount')}</span>
                <span />
              </div>

              {lines.map((l, idx) => {
                const total = lineTotal(l);
                const blank = isBlankLine(l);
                return (
                  <div className={`req-doc__line ${blank ? 'req-doc__line--blank' : ''}`} key={l.key}>
                  <div className={`req-doc__row ${blank ? 'req-doc__row--blank' : ''}`}>
                    <span className="req-doc__col-num">{blank ? '' : idx + 1}</span>
                    <div className="req-doc__cell">
                      <Combobox
                        options={itemOptions}
                        value={l.itemId}
                        onChange={(v) => selectItem(l.key, v)}
                        onSearch={itemPicker.onSearchChange}
                        placeholder={t('Search catalog…')}
                        searchPlaceholder={t('Search name or SKU…')}
                        size="sm"
                        emptyText={itemPicker.searching ? t('Searching…') : t('No catalog matches')}
                        ariaLabel={t('Catalog item')}
                      />
                    </div>
                    <div className="req-doc__cell">
                      <Input size="sm" value={l.description} placeholder={t('Description')} onChange={(e) => updateLine(l.key, { description: e.target.value })} />
                    </div>
                    <div className="req-doc__cell">
                      <Combobox
                        options={supplierOptionsForLine(l)}
                        value={l.supplierId}
                        onChange={(v) => selectLineSupplier(l.key, v)}
                        placeholder={t('Supplier…')}
                        searchPlaceholder={t('Search suppliers…')}
                        size="sm"
                        emptyText={t('No supplier matches')}
                        ariaLabel={t('Preferred supplier')}
                      />
                    </div>
                    <div className="req-doc__cell req-doc__num">
                      <Input size="sm" type="number" value={l.quantity} placeholder="0" onChange={(e) => updateLine(l.key, { quantity: e.target.value })} />
                    </div>
                    <div className="req-doc__cell">
                      <Select size="sm" value={l.uomId} onChange={(v) => updateLine(l.key, { uomId: v })} options={uomOptions} ariaLabel={t('Unit')} searchable />
                    </div>
                    <div className="req-doc__cell req-doc__num">
                      <Input size="sm" type="number" value={l.estimatedUnitPrice} placeholder="0.00" onChange={(e) => updateLine(l.key, { estimatedUnitPrice: e.target.value })} />
                    </div>
                    <span className="req-doc__amount">{total != null ? money(total) : <span className="pp-cell-muted">—</span>}</span>
                    <span className="req-doc__cell">
                      {!blank && (
                        <IconButton aria-label={t('Remove line')} variant="ghost" size="icon-sm" onClick={() => removeLine(l.key)}><Icon.Trash size={15} /></IconButton>
                      )}
                    </span>
                  </div>
                  {!blank && (costCenters.length > 0 || glAccounts.length > 0) && (
                    <div className="req-doc__coding">
                      <span className="req-doc__coding-label">{t('Coding')}</span>
                      <div className="req-doc__coding-field">
                        <Select size="sm" value={l.costCenter} onChange={(v) => updateLine(l.key, { costCenter: v })} options={lineCostCenterOptions} ariaLabel={t('Line cost centre')} searchable />
                      </div>
                      <div className="req-doc__coding-field">
                        <Select size="sm" value={l.glAccount} onChange={(v) => updateLine(l.key, { glAccount: v })} options={lineGlAccountOptions} ariaLabel={t('Line GL account')} searchable />
                      </div>
                    </div>
                  )}
                  </div>
                );
              })}

              <div className="req-doc__foot">
                <Button variant="ghost" size="sm" leadingIcon={<Icon.Plus size={14} />} onClick={addBlankLine}>{t('Add blank line')}</Button>
                <div className="req-doc__totals">
                  <span className="req-doc__totals-label">{t('Estimated total')}</span>
                  <span className="req-doc__totals-value">{money(grandTotal)}</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {codingGroups.length > 0 && (
          <BudgetPanel propertyId={activePropertyId} groups={codingGroups} currency={currency} />
        )}

        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">
                  {requireAttachment ? t('Supporting files (required)') : t('Supporting files')}
                </h3>
                <p className="set-section__hint">
                  {requireAttachment
                    ? t('A photo, supplier quote or specification is required before this requisition can be submitted.')
                    : t('Optional. Attach a photo, supplier quote or specification to help approvers decide.')}
                </p>
              </div>
            </div>

            {existingAttachments > 0 && (
              <div style={{ marginTop: 'var(--space-3)' }}>
                <Banner tone="success">
                  {t('{n} file(s) already attached to this requisition.', { n: existingAttachments })}
                </Banner>
              </div>
            )}

            <div style={{ marginTop: 'var(--space-3)' }}>
              <FileUpload
                files={stagedFiles}
                onChange={setStagedFiles}
                maxSizeMB={maxMb}
                hint={t('PDF, image or Office file up to {n} MB', { n: maxMb })}
                disabled={submitting}
              />
            </div>

            {stagedFiles.length > 0 && (
              <p className="set-section__hint" style={{ marginTop: 'var(--space-2)' }}>
                {t('These upload automatically when you save.')}
              </p>
            )}
          </Card.Body>
        </Card>

        <div className="req-form__actions">
          <Button variant="ghost" onClick={() => navigate('/requisitions')} disabled={submitting}>{t('Cancel')}</Button>
          <Button variant="secondary" loading={submitting} onClick={() => save(false)}>{t('Save draft')}</Button>
          <Button variant="primary" loading={submitting} leadingIcon={<Icon.ArrowUpCircle size={15} />} onClick={() => save(true)}>{t('Save & submit')}</Button>
        </div>
      </div>
    </PageContainer>
  );
}
